// src/components/DisciplineLettersPanel.js
//
// MASTER LIST 10.16 (approved by Bob 2026-09-19). The Letters tab on a discipline case, for officers. Every letter
// the Organization can send on the case (the seven of rulings 18 and 158, and ruling 189's request to cancel),
// each marked ready or not with what is missing named. The officer fills the letter's few fields; RULING 191's
// warning is shown BEFORE the letter is produced, and must be acknowledged; the letter appears with the parent
// International at its head (ruling 188), to Copy or Download. THE PLATFORM DOES NOT SEND IT.
//
// Everything a letter says comes from src\data\disciplineLetters.js (tested); this panel composes no letter text.
// Header data is read the way LetterGroupPanel.js reads it for claims: offices, officeTerms, the committee's seats.
import React, { useEffect, useMemo, useState } from 'react';
import DocumentReview from '../components/DocumentReview';   // section 543: spelling, grammar, facts, prose
import { factsFromRecord } from '../data/documentReview';   // section 543
import { db, storage } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import {
  availableLetters, disciplineLetter, caseHeaderBlocks, riskWarningFor, LETTER_FIELDS, letterInputFromForm,
  CANCELLATION_GROUNDS,
} from '../data/disciplineLetters';
import { PRESERVATION_CLASSES, currentSchedule } from '../data/hearingEvents';
import { affixSignoff, hasSignoff } from '../data/signoff';
import { activate } from '../data/a11yActivate';   // pass 2: role, tabIndex and Enter/Space from one tested helper

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

export function isLetterOfficer(user) {
  return !!(user && (user.isAdmin || user.role === 'admin' || user.role === 'general_chair' || user.role === 'local_chair'));
}

export default function DisciplineLettersPanel({ kase, user }) {
  const officer = isLetterOfficer(user);
  const committee = kase && (kase.generalCommittee || kase.committee);
  const scopeKey = kase && kase.scopeKey;
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState('');
  const [kind, setKind] = useState('');
  const [form, setForm] = useState({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [produced, setProduced] = useState(null);
  const [copied, setCopied] = useState(false);
  const [letterhead, setLetterhead] = useState(null);   // ruling 229: the committee's letterhead banner
  const [signoff, setSignoff] = useState(null);          // ruling 230: THIS officer's own sign-off
  const [sigUrl, setSigUrl] = useState('');
  const [useImage, setUseImage] = useState(false);
  const [adopted, setAdopted] = useState(null);          // ruling 231: the letter AFTER the officer signs off

  useEffect(() => {
    if (!officer || !committee) return undefined;
    let dead = false;
    (async () => {
      try {
        const ids = [committee].concat(scopeKey ? [scopeKey] : []);
        const [offs, terms, cmte, me] = await Promise.all([
          Promise.all(ids.map(id => getDoc(doc(db, 'offices', id)))),
          Promise.all(ids.map(id => getDocs(query(collection(db, 'officeTerms'), where('officeId', '==', id))))),
          getDoc(doc(db, 'committees', committee)),
          user && user.uid ? getDoc(doc(db, 'users', user.uid)) : Promise.resolve(null),
        ]);
        if (dead) return;
        setCtx({
          offices: offs.filter(s => s.exists()).map(s => ({ officeId: s.id, ...s.data() })),
          terms: terms.flatMap(snap => snap.docs.map(d => ({ termId: d.id, ...d.data() }))),
          seats: cmte.exists() && Array.isArray(cmte.data().seats) ? cmte.data().seats : [],
          carrierContacts: [],   // no carrier contact record exists on the platform; the railroad block says so
        });
        setLetterhead(cmte.exists() ? (cmte.data().letterhead || null) : null);
        const so = me && me.exists() ? (me.data().signoff || null) : null;
        setSignoff(so);
        if (so && so.imagePath) { try { setSigUrl(await getDownloadURL(ref(storage, so.imagePath))); } catch { setSigUrl(''); } }
      } catch (e) { if (!dead) setErr('Header data could not be read: ' + (e && e.code ? e.code : e.message)); }
    })();
    return () => { dead = true; };
  }, [officer, committee, scopeKey, user && user.uid]);

  // The case as a letter reads it: its current hearing date comes from the acts, never typed (hearingEvents.js).
  const letterCase = useMemo(() => {
    if (!kase) return null;
    const fallback = kase.hearingLog && kase.hearingLog.hearingDate ? kase.hearingLog.hearingDate : null;
    const sched = currentSchedule(Array.isArray(kase.hearingEvents) ? kase.hearingEvents : [], fallback);
    return { ...kase, hearingDate: (sched && sched.date) || kase.hearingDate || fallback || '' };
  }, [kase]);
  const blocks = useMemo(() => (ctx && letterCase ? caseHeaderBlocks(letterCase, { ...ctx, asOf: letterCase.openedAt || letterCase.createdAt }) : null), [ctx, letterCase]);
  const list = useMemo(() => (blocks ? availableLetters(letterCase, blocks, {}) : []), [blocks, letterCase]);
  const input = useMemo(() => (kind ? letterInputFromForm(kind, form) : {}), [kind, form]);
  const warning = kind ? riskWarningFor(kind, input) : null;
  const preview = useMemo(() => (kind && blocks ? disciplineLetter(kind, letterCase, blocks, input, { letterhead }) : null), [kind, blocks, letterCase, input, letterhead]);

  if (!officer || !kase) return null;

  const reset = () => { setAcknowledged(false); setProduced(null); setCopied(false); setAdopted(null); };
  const choose = (k) => { setKind(k); setForm({}); reset(); };
  const set = (key, value) => { setForm(f => ({ ...f, [key]: value })); reset(); };
  const produce = () => { if (preview && preview.ok && (!warning || acknowledged)) { setProduced(preview.letter); setAdopted(null); } };
  // RULING 231: the signature is affixed ONLY here, on the officer's explicit act on THIS letter.
  const signOff = () => {
    if (!produced) return;
    const r = affixSignoff(produced.text, signoff, { at: new Date(), useImage });
    if (!r.ok) { setErr('Cannot sign off: ' + r.missing.join('; ')); return; }
    setErr('');
    setAdopted({ ...r });
  };
  const current = adopted || produced;
  const copy = async () => { try { await navigator.clipboard.writeText(current.text); setCopied(true); } catch (e) { setErr('Copy failed: ' + e.message); } };
  const download = () => {
    const blob = new Blob([current.text], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = (kase.caseNumber || 'case') + ' - ' + produced.title + (adopted ? ' (signed)' : ' (DRAFT)') + '.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ background: C.panel, border: '1px solid ' + C.line, borderRadius: 8, padding: 16, color: C.text }}>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>The Organization's letters on this case</div>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
        Each letter is produced from the case record. The platform does not send it - you do. Officers only.
      </div>
      {err ? <div style={{ color: C.bad, fontSize: 12, marginBottom: 8 }}>&#9888; {err}</div> : null}
      {!blocks ? <div style={{ fontSize: 12, color: C.dim }}>Reading the header details…</div> : (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 260, flex: '0 0 280px' }}>
            {list.map(l => (
              <div key={l.kind} {...activate(() => choose(l.kind))}
                style={{ padding: '8px 10px', marginBottom: 6, borderRadius: 6, cursor: 'pointer',
                  border: '1px solid ' + (kind === l.kind ? C.accent : C.line) }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{l.title}</div>
                <div style={{ fontSize: 11, color: l.sendable ? C.ok : C.dim }}>
                  {l.sendable ? 'Ready' : 'Needs: ' + [...l.missingFields, ...l.unresolvedBlocks.map(b => 'the ' + b.replace(/_/g, ' ') + ' block')].join('; ')}
                </div>
              </div>
            ))}
          </div>
          {kind ? (
            <div style={{ flex: 1, minWidth: 300 }}>
              {(LETTER_FIELDS[kind] || []).map(fld => (
                <div key={fld.key} className="form-group" style={{ marginBottom: 10 }}>
                  <label className="form-label">{fld.label}</label>
                  {fld.kind === 'text' ? (
                    <input aria-label={fld.label} className="form-input" value={form[fld.key] || ''} onChange={e => set(fld.key, e.target.value)} />
                  ) : fld.kind === 'lines' ? (
                    <textarea aria-label={fld.label} className="form-textarea" rows={4} value={form[fld.key] || ''} onChange={e => set(fld.key, e.target.value)} />
                  ) : fld.kind === 'choice' ? (
                    <select aria-label={fld.label} className="form-input" value={form[fld.key] || ''} onChange={e => set(fld.key, e.target.value)}>
                      <option value="">Choose…</option>
                      {CANCELLATION_GROUNDS.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
                    </select>
                  ) : fld.kind === 'classes' ? (
                    <div>{PRESERVATION_CLASSES.map(c => (
                      <label key={c} style={{ marginRight: 14, fontSize: 13 }}>
                        <input aria-label={c} type="checkbox" checked={(form[fld.key] || []).includes(c)}
                          onChange={() => set(fld.key, (form[fld.key] || []).includes(c) ? (form[fld.key] || []).filter(x => x !== c) : (form[fld.key] || []).concat([c]))} /> {c}
                      </label>
                    ))}</div>
                  ) : null}
                </div>
              ))}
              {warning ? (
                // RULING 191: shown before the letter is produced, acknowledged, never printed in it.
                <div style={{ border: '1px solid ' + C.accent, background: '#2A1F00', borderRadius: 6, padding: 10, marginBottom: 10, fontSize: 12, color: C.accent }}>
                  &#9888; {warning}
                  <label style={{ display: 'block', marginTop: 6, color: C.text }}>
                    <input aria-label="I have read this" type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} /> I have read this
                  </label>
                </div>
              ) : null}
              {preview && !preview.sendable ? (
                <div style={{ fontSize: 12, color: C.dim, marginBottom: 8 }}>
                  Not ready: {[...preview.missingFields, ...preview.unresolvedBlocks.map(b => 'the ' + b.replace(/_/g, ' ') + ' block')].join('; ') || '-'}
                </div>
              ) : null}
              <button className="btn btn-primary" disabled={!preview || !preview.ok || (warning && !acknowledged)} onClick={produce}>Produce draft</button>
              {produced ? (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6, color: adopted ? C.ok : C.accent }}>
                    {adopted ? '✓ SIGNED by you, ' + adopted.signedAt : 'DRAFT — not signed. It carries no signature until you sign off on it below.'}
                  </div>
                  <pre style={{ whiteSpace: 'pre-wrap', background: '#07101C', border: '1px solid ' + C.line, borderRadius: 6, padding: 12, fontSize: 12, color: C.text }}>{current.text}</pre>
                  <DocumentReview title="this letter" kind="discipline" text={current.text} facts={factsFromRecord(letterCase)} today={new Date().toISOString().slice(0, 10)} />
                  {adopted && adopted.usedImage && sigUrl ? (
                    <div style={{ marginBottom: 8 }}><div style={{ fontSize: 11, color: C.dim }}>Signature affixed:</div><img src={sigUrl} alt="Signature" style={{ maxHeight: 50, background: '#fff', padding: 3, borderRadius: 4 }} /></div>
                  ) : null}
                  {/* RULING 231: sign-off is the officer's explicit act; nothing is signed until this button is pressed. */}
                  {!adopted ? (
                    hasSignoff(signoff) ? (
                      <div style={{ border: '1px solid ' + C.line, borderRadius: 6, padding: 10, marginBottom: 8 }}>
                        <div style={{ fontSize: 12, color: C.text, marginBottom: 6 }}>Sign off to adopt this letter under your name. This affixes your signature to THIS letter only.</div>
                        {signoff.imagePath ? (
                          <label style={{ display: 'block', fontSize: 12, color: C.text, marginBottom: 6 }}>
                            <input aria-label="Also affix my signature image" type="checkbox" checked={useImage} onChange={e => setUseImage(e.target.checked)} /> Also affix my signature image
                          </label>
                        ) : null}
                        <button className="btn btn-primary btn-sm" disabled={!preview || !preview.sendable} onClick={signOff}>Sign off &amp; adopt</button>
                        {preview && !preview.sendable ? <span style={{ marginLeft: 10, fontSize: 11, color: C.accent }}>Complete the missing details before you can sign off.</span> : null}
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: C.accent, marginBottom: 8 }}>&#9888; You have no sign-off on file. Set one up in "Your signature sign-off" to sign this letter.</div>
                    )
                  ) : null}
                  <button className="btn btn-secondary" onClick={copy} style={{ marginRight: 8 }}>{copied ? 'Copied' : 'Copy'}</button>
                  <button className="btn btn-secondary" onClick={download}>Download</button>
                  {preview && !preview.sendable ? <span style={{ marginLeft: 10, fontSize: 11, color: C.accent }}>Produced with missing details marked — complete them before you send it.</span> : null}
                </div>
              ) : null}
            </div>
          ) : <div style={{ fontSize: 12, color: C.dim }}>Choose a letter on the left.</div>}
        </div>
      )}
    </div>
  );
}
